import React, { useEffect, useState } from 'react';
import { Card, Typography, Row, Col, Button, Divider, Table, DatePicker } from 'antd';
import {
    ArrowLeftOutlined, TeamOutlined, TrophyOutlined,
    LineChartOutlined, DollarOutlined, AimOutlined, CalendarOutlined
} from '@ant-design/icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    PieChart, Pie, Cell, ResponsiveContainer,
    LineChart, Line, Tooltip as RechartsTooltip, LabelList
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { EnquiryService } from '../../Services/Manage/EnquiryService';
import { SalesTargetService } from '../../Services/Manage/SalesTargetService';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { AddEmployeesService } from '../../Services/Manage/AddEmployeesService';
import { InvoiceService } from '../../Services/Manage/InvoiceService';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const SalesExecReport = () => {
    const perm = getPermission('/enquiry');
    const viewAll = perm.view_all === true;
    const navigate = useNavigate();

    const [segData, setSegData] = useState([]);
    const [wonStatuses, setWonStatuses] = useState([]);
    const [allStatusConfig, setAllStatusConfig] = useState([]);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);

    const [targets, setTargets] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [salesExecNames, setSalesExecNames] = useState([]);

    useEffect(() => {
        if (!viewAll) { navigate('/enquiry', { replace: true }); return; }
        getAllServices();
        loadStatuses();
        loadTargets();
        loadQuotations();
        loadInvoices();
        loadUserMap();
        loadSalesExecutives();
    }, []);

    const loadQuotations = async () => {
        try {
            const { QuotationService } = await import('../../Services/Manage/QuotationService');
            const res = await QuotationService.getAllQuotation();
            setQuotations(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadInvoices = async () => {
        try {
            const res = await InvoiceService.getAllInvoice();
            setInvoices(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadUserMap = async () => {
        try {
            const { UserService } = await import('../../Services/Manage/UserService');
            const res = await UserService.getAllUsers();
            const list = res?.data || res || [];
            const map = {};
            list.forEach((u) => {
                const uid = u.uid || u.user_id || u.id;
                const nm = u.name || u.user_name || u.username;
                if (uid && nm) map[String(uid).trim()] = nm;
            });
            setUserMap(map);
        } catch (e) { console.error(e); }
    };

    const loadSalesExecutives = async () => {
        try {
            const res = await AddEmployeesService.getByDepartment("Sales");
            const names = (res.data || []).map(e => e.name).filter(Boolean);
            setSalesExecNames(names);
        } catch (e) { console.error(e); }
    };

    const getAllServices = async () => {
        try {
            const request = await EnquiryService.getAllEnquiries();
            setSegData(request.data || []);
        } catch (e) { console.error(e); }
    };

    const loadStatuses = async () => {
        try {
            const { ConfigurationService } = await import('../../Services/Manage/ConfigurationService');
            const res = await ConfigurationService.getByType("enquiry_status");
            const all = res.data || [];
            setAllStatusConfig(all);
            setWonStatuses(all.filter(item => item.label.trim().toLowerCase() === "won").map(item => item.label.trim()));
        } catch (e) { console.error(e); }
    };

    const loadTargets = async () => {
        try {
            const res = await SalesTargetService.getAll();
            setTargets(res.data || []);
        } catch (e) { console.error(e); }
    };

    const parseItems = (items) => {
        if (Array.isArray(items)) return items;
        try { return JSON.parse(items || "[]"); } catch { return []; }
    };

    const filteredData = dateRange && dateRange[0] && dateRange[1]
        ? segData.filter(d => {
            const date = dayjs(d.eq_date);
            return date.isValid()
                && date.isSameOrAfter(dateRange[0], 'day')
                && date.isSameOrBefore(dateRange[1], 'day');
        })
        : segData;
    const amountOf = (d) => {
        let amt = parseFloat(String(d.total_amount ?? 0).replace(/[^0-9.-]/g, "")) || 0;
        if (!amt) {
            const s = parseItems(d.service_items);
            const p = parseItems(d.product_items);
            amt = [...s, ...p].reduce((a, it) => a + (Number(it.unit_price) || 0), 0);
        }
        return amt;
    };


    // replace:
    const execNamesSet = new Set();
    filteredData.forEach(d => {
        const assignedNames = (d.assigned_to || "").split(',').map(s => s.trim()).filter(Boolean);
        assignedNames.forEach(n => execNamesSet.add(n));
        if (assignedNames.length === 0) {
            const creatorName = userMap[String(d.addedby ?? "").trim()];
            if (creatorName && salesExecNames.includes(creatorName)) execNamesSet.add(creatorName);
        }
    });
    invoices.forEach(inv => {
        const names = (inv.sales_executive || "").split(',').map(s => s.trim()).filter(Boolean);
        names.forEach(n => execNamesSet.add(n));
        if (names.length === 0) {
            const creatorName = userMap[String(inv.added_by ?? "").trim()];
            if (creatorName && salesExecNames.includes(creatorName)) execNamesSet.add(creatorName);
        }
    });
    const execNames = [...execNamesSet];
    // Map exec name -> total revenue target (sums all their targets, incl. team member splits)
    const targetByName = {};
    targets.forEach((t) => {
        if (t.sales_executive_name) {
            targetByName[t.sales_executive_name] =
                (targetByName[t.sales_executive_name] || 0) + Number(t.revenue_target || 0);
        } else {
            const mt = Array.isArray(t.member_targets) ? t.member_targets
                : (() => { try { return JSON.parse(t.member_targets || "[]"); } catch { return []; } })();
            mt.forEach((m) => {
                if (m.member_name) {
                    targetByName[m.member_name] =
                        (targetByName[m.member_name] || 0) + Number(m.revenue_target || 0);
                }
            });
        }
    });

    const colors = ['#1677ff', '#52c41a', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];

    const countStatus = (rows, label) =>
        rows.filter(d => d.eq_status?.trim().toLowerCase() === label.toLowerCase()).length;

    // Revenue per executive, sourced from converted quotations (date-scoped to the selected report period)
// Collected (received) amount per executive, summed from each matching invoice's payments
    const collectedOf = (inv) => (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);

    const calcCollectedRevenue = (name) => {
        return invoices
            .filter((inv) => {
                const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.includes(name)) return true;
                if (names.length === 0 && inv.added_by) {
                    const resolvedName = userMap[String(inv.added_by).trim()];
                    return resolvedName === name;
                }
                return false;
            })
            .filter((inv) => {
                if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
                const dateStr = inv.invoice_date || inv.updated_on || inv.added_on;
                if (!dateStr) return true;
                const d = dayjs(dateStr);
                return d.isValid() && d.isSameOrAfter(dateRange[0], 'day') && d.isSameOrBefore(dateRange[1], 'day');
            })
            .reduce((sum, inv) => sum + collectedOf(inv), 0);
    };

    // Won deals per executive, sourced from CONVERTED quotations (date-scoped to the selected report period)
    const calcWonDealsFromQuotations = (name) => {
        return quotations
            .filter((q) => (q.quotation_status || "").trim().toLowerCase() === "converted")
            .filter((q) => {
                const names = (q.assigned_to || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.includes(name)) return true;
                if (names.length === 0 && q.added_by) {
                    const resolvedName = userMap[String(q.added_by).trim()];
                    return resolvedName === name;
                }
                return false;
            })
            .filter((q) => {
                if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
                const dateStr = q.date || q.updated_on || q.added_on;
                if (!dateStr) return true;
                const d = dayjs(dateStr);
                return d.isValid() && d.isSameOrAfter(dateRange[0], 'day') && d.isSameOrBefore(dateRange[1], 'day');
            })
            .length;
    };

    const execs = execNames.map(name => {
        const rows = filteredData.filter(d => {
            const assignedNames = (d.assigned_to || "").split(',').map(s => s.trim()).filter(Boolean);
            const isAssigned = assignedNames.includes(name);
            const isCreator = assignedNames.length === 0 &&
                salesExecNames.includes(name) &&
                userMap[String(d.addedby ?? "").trim()] === name;
            return isAssigned || isCreator;
        });

const totalLeads = rows.length;
        const contacted = countStatus(rows, "Contacted");
        const demo = countStatus(rows, "Demo Scheduled");
        const quotation = countStatus(rows, "Quotation Sent");
        const negotiation = countStatus(rows, "Negotiation");
        const wonDeals = calcWonDealsFromQuotations(name);
        const revenue = calcCollectedRevenue(name);
        const conversion = totalLeads ? Number(((wonDeals / totalLeads) * 100).toFixed(2)) : 0;
        const target = targetByName[name] || 0;
        const avgDeal = wonDeals ? Math.round(revenue / wonDeals) : 0;
        const achievement = target ? Number(Math.min((revenue / target) * 100, 100).toFixed(2)) : 0;
        return { name, totalLeads, contacted, demo, quotation, negotiation, wonDeals, revenue, conversion, avgDeal, target, achievement };
    }).sort((a, b) => b.totalLeads - a.totalLeads);

    // ---- Top summary stats ----
    const totalLeads = execs.reduce((s, e) => s + e.totalLeads, 0);
    const wonDeals = execs.reduce((s, e) => s + e.wonDeals, 0);
    const totalRevenue = execs.reduce((s, e) => s + e.revenue, 0);
    const conversionRate = totalLeads ? ((wonDeals / totalLeads) * 100).toFixed(2) : "0.00";

    const avgDealValue = wonDeals ? Math.round(totalRevenue / wonDeals) : 0;


    const revenueTarget = execs.reduce((s, e) => s + e.target, 0);

    const topStats = [
        { title: "Total Leads", value: totalLeads, color: "#1677ff", bg: "#f0f7ff", icon: <TeamOutlined /> },
        { title: "Won Deals", value: wonDeals, color: "#52c41a", bg: "#f6ffed", icon: <TrophyOutlined /> },
        { title: "Conversion Rate", value: `${conversionRate}%`, color: "#fa8c16", bg: "#fff7e6", icon: <LineChartOutlined /> },
        { title: "Total Revenue", value: `₹ ${totalRevenue.toLocaleString('en-IN')}`, color: "#722ed1", bg: "#f9f0ff", icon: <DollarOutlined /> },
        { title: "Average Deal Value", value: `₹ ${avgDealValue.toLocaleString('en-IN')}`, color: "#13c2c2", bg: "#f0fffe", icon: <AimOutlined /> },

        { title: "Revenue Target", value: `₹ ${revenueTarget.toLocaleString('en-IN')}`, color: "#eb2f96", bg: "#fff0f6", icon: <DollarOutlined /> },
    ];

    // ---- Revenue by exec (bar) ----
    const revenueChartData = execs.map(e => ({ name: e.name, revenue: e.revenue }));

    // ---- Leads by status (donut) ----
    const statusColors = ["#1677ff", "#13c2c2", "#fa8c16", "#722ed1", "#52c41a", "#eb2f96", "#f5222d"];
const statusData = allStatusConfig.map((s, i) => {
        const isWonLabel = wonStatuses.includes(s.label.trim());
        return {
            name: s.label,
            // "Won" count comes from converted quotations (wonDeals), matching the
            // rest of the report, instead of relying on the enquiry's eq_status
            // label which can lag behind the actual conversion.
            value: isWonLabel ? wonDeals : filteredData.filter(d => d.eq_status?.trim() === s.label.trim()).length,
            color: statusColors[i % statusColors.length],
        };
    }).filter(s => s.value > 0);
    const statusTotal = statusData.reduce((a, b) => a + b.value, 0);

    // ---- Conversion rate by exec (line) ----
    // ---- Conversion rate by exec (line) ----
    const conversionChartData = execs.map(e => ({ name: e.name, rate: e.conversion }));



const topWonDeals = quotations
        .filter((q) => (q.quotation_status || "").trim().toLowerCase() === "converted")
        .filter((q) => {
            if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
            const dateStr = q.date || q.updated_on || q.added_on;
            if (!dateStr) return true;
            const d = dayjs(dateStr);
            return d.isValid() && d.isSameOrAfter(dateRange[0], 'day') && d.isSameOrBefore(dateRange[1], 'day');
        })
        .map((q) => {
            const names = (q.assigned_to || "").split(',').map(s => s.trim()).filter(Boolean);
            const exec = names.find(n => salesExecNames.includes(n))
                || (() => { const n = userMap[String(q.added_by || "").trim()]; return salesExecNames.includes(n) ? n : null; })()
                || "-";
            // Value is the deal/quoted size — what the customer agreed to — so the
            // "Top" ranking reflects biggest deals, not how much has been collected.
            return {
                eq_id: q.qu_no,
                dealName: q.title || q.project || "-",
                customer: q.customer_name || q.customer_company || "-",
                exec,
                value: Number(q.grand_total_saved || q.grand_total || 0),
                wonDate: q.date || q.updated_on,
            };
        })
        .filter(w => w.exec !== "-")
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

    // ---- Leads by Source ----
    const sourceColors = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#bfbfbf"];
    const sourceCounts = {};
    filteredData.forEach(d => {
        const src = d.eq_source?.trim() || "Others";
        sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    });
    const sourceTotal = Object.values(sourceCounts).reduce((a, b) => a + b, 0);
    const leadsBySource = Object.entries(sourceCounts)
        .map(([name, count], i) => ({
            name, count,
            pct: sourceTotal ? ((count / sourceTotal) * 100).toFixed(2) : "0.00",
            color: sourceColors[i % sourceColors.length],
        }))
        .sort((a, b) => b.count - a.count);

    // ---- Performance Insights ----
    const insights = [];
    if (execs.length) {
        const topRev = [...execs].sort((a, b) => b.revenue - a.revenue)[0];
        if (topRev && topRev.revenue > 0) insights.push({ type: 'good', text: `${topRev.name} has the highest revenue at ₹${topRev.revenue.toLocaleString('en-IN')}.` });
        const lowConv = [...execs].filter(e => e.totalLeads > 0).sort((a, b) => a.conversion - b.conversion)[0];
        if (lowConv) insights.push({ type: 'warn', text: `${lowConv.name} has the lowest conversion rate. Focus on lead follow-up.` });
    }
    if (leadsBySource.length) {
        const topTwo = leadsBySource.slice(0, 2).map(s => s.name).join(" and ");
        insights.push({ type: 'info', text: `${topTwo} are the top lead sources.` });
    }

    const StatCard = ({ title, value, color, bg, icon }) => (
        <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Sales Executive Performance Report</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Reports &gt; Sales Executive Performance</Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DatePicker.RangePicker
                        value={dateRange}
                        onChange={(vals) => setDateRange(vals)}
                        format="DD MMM YYYY"
                        allowClear={false}
                        suffixIcon={<CalendarOutlined />}
                        style={{ borderRadius: 8 }}
                    />
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/enquiry')}>Back to Dashboard</Button>
                </div>
            </div>

            {/* Top Stat Cards */}
            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    {topStats.map((card, i) => (
                        <Col xs={24} sm={12} md={8} lg={4} key={i}>
                            <StatCard {...card} />
                        </Col>
                    ))}
                </Row>
            </div>


            {/* Charts Row */}
            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    {/* Revenue by Sales Executive */}
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Revenue by Sales Executive</Typography.Text>
                            {revenueChartData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📊</span>
                                    <span style={{ marginTop: 6 }}>No revenue data yet</span>
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 220, marginTop: 8 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={revenueChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataIndex="name" dataKey="name" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                            <RechartsTooltip formatter={(v) => `₹ ${Number(v).toLocaleString('en-IN')}`} contentStyle={{ fontSize: 11 }} />
                                            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                                                {revenueChartData.map((e, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                                                <LabelList dataKey="revenue" position="top" style={{ fontSize: 9, fill: '#555' }} formatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Leads by Status */}
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Leads by Status (Overall)</Typography.Text>
                            {statusData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📊</span>
                                    <span style={{ marginTop: 6 }}>No status data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 150, height: 150, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip formatter={(value, name) => [`${value} (${statusTotal ? Math.round((value / statusTotal) * 100) : 0}%)`, name]} contentStyle={{ fontSize: 11 }} />
                                                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                                                    {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#222' }}>{statusTotal}</div>
                                            <div style={{ fontSize: 10, color: '#888' }}>Total Leads</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {statusData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({statusTotal ? Math.round((e.value / statusTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Conversion Rate by Sales Executive */}
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Conversion Rate by Sales Executive</Typography.Text>
                            {conversionChartData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📈</span>
                                    <span style={{ marginTop: 6 }}>No conversion data yet</span>
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 220, marginTop: 8 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={conversionChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                                            <RechartsTooltip formatter={(v) => `${v}%`} contentStyle={{ fontSize: 11 }} />
                                            <Line type="monotone" dataKey="rate" stroke="#52c41a" strokeWidth={2.5} dot={{ r: 4, fill: '#52c41a' }} activeDot={{ r: 6 }}>
                                                <LabelList dataKey="rate" position="top" style={{ fontSize: 9, fill: '#555' }} formatter={(v) => `${v}%`} />
                                            </Line>
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Full Executive Table */}
            <div style={{ padding: '8px 16px 16px' }}>
                <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                    <Typography.Text strong>Detailed Performance by Sales Executive</Typography.Text>
                    <Divider style={{ margin: '8px 0' }} />
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="name"
                        dataSource={execs}
                        scroll={{ x: 'max-content' }}
                        summary={() => {
                            const sum = (k) => execs.reduce((a, e) => a + (e[k] || 0), 0);
                            const avgConv = execs.length ? (execs.reduce((a, e) => a + e.conversion, 0) / execs.length).toFixed(2) : "0.00";
                            return (
                                <Table.Summary.Row style={{ background: '#fafafa' }}>
                                    <Table.Summary.Cell index={0}><b>Total / Average</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="center"><b style={{ color: '#1677ff' }}>{sum('totalLeads')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="center"><b>{sum('contacted')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="center"><b>{sum('demo')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="center"><b>{sum('quotation')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="center"><b>{sum('negotiation')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="center"><b style={{ color: '#52c41a' }}>{sum('wonDeals')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="center"><b style={{ color: '#fa8c16' }}>{avgConv}%</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} align="center"><b style={{ color: '#722ed1' }}>₹{sum('revenue').toLocaleString('en-IN')}</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={9} align="center"><b style={{ color: '#13c2c2' }}>₹{(sum('wonDeals') ? Math.round(sum('revenue') / sum('wonDeals')) : 0).toLocaleString('en-IN')}</b></Table.Summary.Cell>                                    <Table.Summary.Cell index={11} align="center"><b style={{ color: '#52c41a' }}>{(() => { const t = sum('target'); const r = sum('revenue'); return t ? Math.min((r / t) * 100, 100).toFixed(2) : "0.00"; })()}%</b></Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                        columns={[
                            {
                                title: 'Sales Executive', dataIndex: 'name', key: 'name', fixed: 'left',
                                render: (name, _, idx) => (
                                    <span
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                        onClick={() => navigate(`/enquiry/sales-performance/${encodeURIComponent(name)}`)}
                                    >
                                        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: colors[idx % colors.length], fontWeight: 700 }}>
                                            {name.charAt(0).toUpperCase()}
                                        </span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1677ff', textDecoration: 'underline' }}>{name}</span>
                                    </span>
                                ),
                            },
                            { title: 'Total Leads', dataIndex: 'totalLeads', key: 'totalLeads', align: 'center', render: (v) => <span style={{ fontWeight: 700, color: '#1677ff' }}>{v}</span> },
                            { title: 'Contacted', dataIndex: 'contacted', key: 'contacted', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#333' }}>{v}</span> },
                            { title: 'Demo Scheduled', dataIndex: 'demo', key: 'demo', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#333' }}>{v}</span> },
                            { title: 'Quotation Sent', dataIndex: 'quotation', key: 'quotation', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#333' }}>{v}</span> },
                            { title: 'Negotiation', dataIndex: 'negotiation', key: 'negotiation', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#333' }}>{v}</span> },
                            { title: 'Won Deals', dataIndex: 'wonDeals', key: 'wonDeals', align: 'center', render: (v) => <span style={{ fontWeight: 700, color: '#52c41a' }}>{v}</span> },
                            {
                                title: 'Conversion', dataIndex: 'conversion', key: 'conversion', align: 'center',
                                render: (v) => (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#fa8c16' }}>{v}%</span>
                                        <div style={{ width: 60, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
                                            <div style={{ width: `${Math.min(v, 100)}%`, height: '100%', background: '#fa8c16', borderRadius: 2 }} />
                                        </div>
                                    </div>
                                ),
                            },
                            { title: 'Total Revenue', dataIndex: 'revenue', key: 'revenue', align: 'center', render: (v) => <span style={{ fontWeight: 700, color: '#722ed1' }}>₹{v.toLocaleString('en-IN')}</span> },
                            { title: 'Avg. Deal Value', dataIndex: 'avgDeal', key: 'avgDeal', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#13c2c2' }}>₹{v.toLocaleString('en-IN')}</span> },
                            { title: 'Target', dataIndex: 'target', key: 'target', align: 'center', render: (v) => <span style={{ fontWeight: 600, color: '#eb2f96' }}>₹{v.toLocaleString('en-IN')}</span> },
                            { title: 'Achievement', dataIndex: 'achievement', key: 'achievement', align: 'center', render: (v) => <span style={{ fontWeight: 700, color: '#52c41a' }}>{v}%</span> },
                        ]}
                    />
                </Card>
            </div>

            {/* Bottom Row: Top Won Deals / Leads by Source / Performance Insights */}
            <div style={{ padding: '0 16px 16px' }}>
                <Row gutter={[8, 8]} align="stretch">
                    {/* Top Won Deals */}
                    <Col xs={24} lg={10}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Top Won Deals</Typography.Text>
                            <Divider style={{ margin: '8px 0' }} />
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="eq_id"
                                dataSource={topWonDeals}
                                scroll={{ x: 'max-content' }}
                                locale={{ emptyText: 'No won deals yet' }}
                                columns={[
                                    // { title: 'Deal Name', dataIndex: 'dealName', key: 'dealName', render: (v) => <span style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>{v}</span> },
                                    { title: 'Customer', dataIndex: 'customer', key: 'customer', render: (v) => <span style={{ fontSize: 12, color: '#555' }}>{v}</span> },
                                    { title: 'Sales Executive', dataIndex: 'exec', key: 'exec', render: (v) => <span style={{ fontSize: 12, color: '#555' }}>{v}</span> },
                                    { title: 'Deal Value', dataIndex: 'value', key: 'value', align: 'right', render: (v) => <span style={{ fontSize: 12, fontWeight: 700, color: '#722ed1' }}>₹{v.toLocaleString('en-IN')}</span> },
                                    { title: 'Won Date', dataIndex: 'wonDate', key: 'wonDate', align: 'center', render: (v) => <span style={{ fontSize: 12, color: '#52c41a' }}>{v ? dayjs(v).format('DD-MMM-YYYY') : '-'}</span> },
                                ]}
                            />
                        </Card>
                    </Col>

                    {/* Leads by Source */}
                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Leads by Source</Typography.Text>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', marginBottom: 8 }}>
                                <span style={{ flex: 1 }}>Source</span>
                                <span style={{ width: 50, textAlign: 'center' }}>Leads</span>
                                <span style={{ flex: 1, textAlign: 'right' }}>Percentage</span>
                            </div>
                            {leadsBySource.length === 0 ? (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>No source data yet</Typography.Text>
                            ) : (
                                leadsBySource.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < leadsBySource.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                                        <span style={{ flex: 1, fontSize: 12, color: '#333' }}>{s.name}</span>
                                        <span style={{ width: 50, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#222' }}>{s.count}</span>
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                            <div style={{ width: 50, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ width: `${s.pct}%`, height: '100%', background: s.color, borderRadius: 3 }} />
                                            </div>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#333', minWidth: 42, textAlign: 'right' }}>{s.pct}%</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </Card>
                    </Col>

                    {/* Performance Insights */}
                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Performance Insights</Typography.Text>
                            <Divider style={{ margin: '8px 0' }} />
                            {insights.length === 0 ? (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>No insights yet</Typography.Text>
                            ) : (
                                insights.map((ins, i) => {
                                    const dot = ins.type === 'good' ? '#52c41a' : ins.type === 'warn' ? '#fa8c16' : '#1677ff';
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                                            <span style={{ color: dot, fontSize: 14, lineHeight: '18px', flexShrink: 0 }}>●</span>
                                            <span style={{ fontSize: 12, color: '#444', lineHeight: '18px' }}>{ins.text}</span>
                                        </div>
                                    );
                                })
                            )}
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                Report Period: {dateRange?.[0]?.format('DD MMM YYYY') ?? dayjs().startOf('month').format('DD MMM YYYY')} - {dateRange?.[1]?.format('DD MMM YYYY') ?? dayjs().format('DD MMM YYYY')}
                            </Typography.Text>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default SalesExecReport;